import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, useTier } from "@/context/AuthContext";
import { apiClient } from "@/lib/api";

const TOKEN_KEY = "cdi.auth.token";
const IMPERSONATION_KEY = "cdi.impersonation.admin";
// Admin endpoints live under `/admin` on the shared apiClient. Keep a
// thin facade so every existing call site (`client.get("/users")`,
// `client.patch("/users/:id/tier")`, …) resolves to `/api/admin/...`
// without touching the ~30 downstream invocations below. The shared
// apiClient already carries the Bearer-token request interceptor and
// the fixed global 401 handler, so this file no longer builds its
// own axios instance.
const client = {
  get:    (p, ...a) => apiClient.get("/admin" + p, ...a),
  post:   (p, ...a) => apiClient.post("/admin" + p, ...a),
  patch:  (p, ...a) => apiClient.patch("/admin" + p, ...a),
  delete: (p, ...a) => apiClient.delete("/admin" + p, ...a),
};

// Compact Admin Dashboard. Backend enforces admin-only access; the
// frontend only handles UX + 403/404 rendering. Everything data-driven
// from backend responses — no tier logic re-implemented client-side.
export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const { isAdmin } = useTier();
  const [tab, setTab] = useState("users");
  // Sidebar-level regression alert: mirrors the count from
  // GET /admin/regression-checks (recent_issues_24h). Refreshed
  // every 60s + on tab focus so a new failure surfaces quickly.
  const [regressionAlert, setRegressionAlert] = useState(0);
  useEffect(() => {
    if (!user || !isAdmin) return;
    let cancelled = false;
    const load = () => {
      client.get("/regression-checks?limit=1")
        .then((r) => { if (!cancelled) setRegressionAlert(r.data?.recent_issues_24h || 0); })
        .catch(() => { /* silent — sidebar just won't show a badge */ });
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [user, isAdmin]);
  // Wait for AuthProvider's /auth/session to resolve before deciding
  // access. Redirecting on the pre-hydration null user would boot
  // legitimate admins off the page on hard refresh.
  if (loading) return <div className="p-6 text-sm" style={{ color: "#9a9a9a" }} data-testid="admin-dashboard-loading">Loading…</div>;
  if (!user || !isAdmin) return <Navigate to="/" replace />;

  const tabs = [
    { id: "users",  label: "Users" },
    { id: "stats",  label: "Tiers & Analytics" },
    { id: "cols",   label: "Collections" },
    { id: "items",  label: "Items" },
    { id: "logs",   label: "Logs" },
    { id: "audit",  label: "System Audit" },
    { id: "reg",    label: "Regression Monitor", badge: "regression" },
    { id: "imp",    label: "Impersonation Audit" },
    { id: "health", label: "System Health" },
    { id: "datahealth", label: "Data Health" },
  ];

  return (
    // Inline shell — the app-level <Layout /> already provides the
    // sticky header (Home, FAQ, Admin, Settings, Logout) so this
    // component only owns the admin sidebar + tab content that sit
    // beneath it. `min-h-[calc(100vh-4rem)]` reserves the full viewport
    // minus the 64px header so the dark canvas fills the visible area.
    <div className="flex min-h-[calc(100vh-4rem)]" style={{ background: "#0f0f0f", color: "#f7f7f7" }} data-testid="admin-dashboard">
      <nav className="w-56 border-r shrink-0" style={{ borderColor: "#2a2a2a", background: "#1a1a1a" }}>
        <div className="p-4 font-bold" style={{ color: "#C9A86A", letterSpacing: 1 }}>CDI Admin</div>
        <ul>
          {tabs.map((t) => (
            <li key={t.id}>
              <button
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className="w-full text-left px-4 py-2 text-sm flex items-center justify-between"
                style={{
                  background: tab === t.id ? "#231d10" : "transparent",
                  color: tab === t.id ? "#C9A86A" : "#c9c9c9",
                  borderLeft: tab === t.id ? "3px solid #C9A86A" : "3px solid transparent",
                }}
              >
                <span>{t.label}</span>
                {t.badge === "regression" && regressionAlert > 0 && (
                  <span
                    data-testid="admin-tab-reg-badge"
                    style={{
                      background: "#c94a4a", color: "#fff",
                      fontSize: 10, fontWeight: 700,
                      padding: "1px 6px", borderRadius: 8, lineHeight: "14px",
                    }}
                    title={`${regressionAlert} issue(s) detected in the last 24h`}
                  >
                    {regressionAlert}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex-1 p-6 overflow-auto min-w-0">
        {tab === "users"  && <Users  />}
        {tab === "stats"  && <Stats  />}
        {tab === "cols"   && <Cols   />}
        {tab === "items"  && <Items  />}
        {tab === "logs"   && <Logs   />}
        {tab === "audit"  && <AuditWrite />}
        {tab === "reg"    && <RegressionMonitor />}
        {tab === "imp"    && <ImpersonationAudit />}
        {tab === "health" && <Health />}
        {tab === "datahealth" && <DataHealth />}
      </div>
    </div>
  );
}

function useFetch(path, deps = []) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    client.get(path).then(r => { if (!cancelled) { setData(r.data); setErr(null); } })
      .catch(e => { if (!cancelled) setErr(e?.response?.status || "err"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, deps); // eslint-disable-line
  return { data, err, loading, reload: () => setLoading(true) };
}

const cell = { padding: "8px 10px", borderBottom: "1px solid #2a2a2a", fontSize: 12 };
const th = { ...cell, color: "#9a9a9a", fontWeight: 600, textAlign: "left", background: "#0f0f0f" };

function Table({ headers, rows, testid }) {
  return (
    <div className="overflow-auto" style={{ border: "1px solid #2a2a2a", borderRadius: 6 }}>
      <table data-testid={testid} className="w-full">
        <thead><tr>{headers.map((h, i) => <th key={typeof h === "string" ? h : `h-${i}`} style={th}>{h}</th>)}</tr></thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
}

function SearchInput({ value, onChange, placeholder, testid }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full max-w-sm px-3 py-2 text-sm rounded-md mb-3"
      style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", color: "#f7f7f7" }}
    />
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return "—"; }
}

// --------- Users -----------------------------------------------------
function csvEscape(v) {
  const s = v === null || v === undefined ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(filename, headers, rows) {
  const lines = [headers.map(csvEscape).join(",")];
  for (const row of rows) lines.push(row.map(csvEscape).join(","));
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

async function impersonate(userId, email, adminEmail) {
  if (!window.confirm(`Impersonate ${email}?\n\nA red banner will appear at the top of every page while you are impersonating. Click "Return to admin" to restore your admin session.`)) return;
  try {
    // Snapshot the current admin token BEFORE the endpoint sets a new cookie/token.
    // The impersonation banner uses this to offer a one-click return path.
    const adminToken = localStorage.getItem(TOKEN_KEY);
    const r = await client.post(`/users/${userId}/impersonate`);
    const tok = r.data?.access_token;
    if (tok) {
      try {
        localStorage.setItem(TOKEN_KEY, tok);
        if (adminToken) {
          localStorage.setItem(IMPERSONATION_KEY, JSON.stringify({
            admin_token: adminToken,
            admin_email: adminEmail || null,
            target_email: email,
            started_at: new Date().toISOString(),
          }));
        }
      } catch { /* private mode */ }
    }
    toast.success(`Now impersonating ${email}. Redirecting…`);
    setTimeout(() => { window.location.href = "/"; }, 700);
  } catch (e) {
    toast.error(e?.response?.data?.detail?.error || "Impersonation failed");
  }
}

function Users() {
  const { user: currentUser } = useAuth();
  const [tick, setTick] = useState(0);
  const { data, err, loading } = useFetch("/users", [tick]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Delete-user modal state. `target` carries the row object so the
  // modal can display the email for the confirmation copy.
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const filtered = useMemo(() => {
    const list = data || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(u => (u.email || "").toLowerCase().includes(needle));
  }, [data, q]);

  const allSelected = filtered.length > 0 && filtered.every(u => selected.has(u.user_id));
  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach(u => next.delete(u.user_id));
      else filtered.forEach(u => next.add(u.user_id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) return <p>Loading…</p>;
  if (err === 403) return <p data-testid="admin-users-forbidden">Forbidden.</p>;

  const doAction = async (id, fn, msg) => {
    try { await fn(); toast.success(msg); setTick(t => t + 1); }
    catch (e) { toast.error(e?.response?.data?.detail?.error || "Action failed"); }
  };

  const doBulk = async (label, body) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`${label} for ${ids.length} user(s)?`)) return;
    // Snapshot each selected user's previous state so we can undo per-user.
    // `filtered` is the currently-visible list; anyone selected must be in
    // it (checkboxes only render for filtered rows).
    const snapshot = filtered
      .filter(u => selected.has(u.user_id))
      .map(u => ({ id: u.user_id, tier: u.tier, banned: !!u.banned }));
    setBulkBusy(true);
    try {
      const r = await client.post("/users/bulk_action", { ...body, ids });
      const affected = r.data?.modified ?? ids.length;
      const undo = async () => {
        try {
          if (body.action === "ban") {
            // Group by original banned state; may need up to two calls.
            const bannedIds = snapshot.filter(s => s.banned).map(s => s.id);
            const unbannedIds = snapshot.filter(s => !s.banned).map(s => s.id);
            const calls = [];
            if (bannedIds.length) calls.push(client.post("/users/bulk_action", { ids: bannedIds, action: "ban", banned: true }));
            if (unbannedIds.length) calls.push(client.post("/users/bulk_action", { ids: unbannedIds, action: "ban", banned: false }));
            await Promise.all(calls);
          } else if (body.action === "tier") {
            // Group by original tier; one call per unique previous tier.
            const buckets = {};
            for (const s of snapshot) {
              const t = ["trial", "basic", "premium"].includes(s.tier) ? s.tier : "trial";
              (buckets[t] = buckets[t] || []).push(s.id);
            }
            const calls = Object.entries(buckets).map(([tier, groupIds]) =>
              client.post("/users/bulk_action", { ids: groupIds, action: "tier", tier, ...(tier === "premium" ? { premium_plan: "monthly" } : {}) })
            );
            await Promise.all(calls);
          }
          toast.success(`Undone — ${snapshot.length} user(s) restored`);
          setTick(t => t + 1);
        } catch {
          toast.error("Undo failed");
        }
      };
      toast.success(`${label} — ${affected} of ${ids.length} updated`, {
        duration: 10000,
        action: { label: "Undo", onClick: undo },
      });
      setSelected(new Set());
      setTick(t => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const exportCsv = () => {
    const headers = ["Email", "Tier", "Verified", "Joined", "Last Login", "Collections", "Items", "Banned", "Basic Purchased", "Premium Plan"];
    const rows = filtered.map(u => [
      u.email,
      u.tier,
      u.email_verified ? "yes" : "no",
      u.date_joined || "",
      u.last_login || "",
      u.collections_count,
      u.items_count,
      u.banned ? "yes" : "no",
      u.basic_purchased ? "yes" : "no",
      u.premium_status || "",
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`cdi-users-${stamp}.csv`, headers, rows);
    toast.success(`Exported ${rows.length} user(s) to CSV`);
  };

  const bulkBtn = (label, testid, onClick, color) => (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={selected.size === 0 || bulkBusy}
      className="px-3 py-2 text-xs rounded-md"
      style={{
        background: selected.size === 0 ? "#1a1a1a" : "#231d10",
        color: selected.size === 0 ? "#6a6a6a" : color,
        border: `1px solid ${selected.size === 0 ? "#2a2a2a" : color}`,
        cursor: selected.size === 0 ? "not-allowed" : "pointer",
      }}
    >{label}</button>
  );

  return (
    <>
      <h2 className="text-lg font-semibold mb-4">Users ({filtered.length}{q ? ` of ${data?.length || 0}` : ""})</h2>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Search by email…" testid="admin-users-search" />
        <button
          data-testid="admin-users-export-csv"
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="px-3 py-2 text-xs rounded-md mb-3"
          style={{
            background: filtered.length === 0 ? "#1a1a1a" : "#231d10",
            color: filtered.length === 0 ? "#6a6a6a" : "#C9A86A",
            border: `1px solid ${filtered.length === 0 ? "#2a2a2a" : "#C9A86A"}`,
            cursor: filtered.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Download CSV ({filtered.length})
        </button>
        <button
          data-testid="admin-users-send-digest"
          onClick={async () => {
            if (!window.confirm("Send the weekly digest email to every admin in CDI_ADMIN_EMAILS right now?")) return;
            try {
              const r = await client.post("/digest/send");
              toast.success(`Digest sent to ${r.data?.recipients ?? "?"} admin(s)`);
            } catch (e) { toast.error(e?.response?.data?.detail || "Digest send failed"); }
          }}
          className="px-3 py-2 text-xs rounded-md mb-3"
          style={{ background: "#231d10", color: "#C9A86A", border: "1px solid #C9A86A", cursor: "pointer" }}
        >
          Send Digest Now
        </button>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="admin-users-bulk-toolbar">
        <span className="text-xs" style={{ color: "#9a9a9a" }}>
          Bulk ({selected.size} selected):
        </span>
        {bulkBtn("→ Trial", "admin-users-bulk-tier-trial", () => doBulk("Set tier to Trial", { action: "tier", tier: "trial" }), "#c86a6a")}
        {bulkBtn("→ Basic", "admin-users-bulk-tier-basic", () => doBulk("Set tier to Basic", { action: "tier", tier: "basic" }), "#C9A86A")}
        {bulkBtn("→ Premium", "admin-users-bulk-tier-premium", () => doBulk("Set tier to Premium", { action: "tier", tier: "premium", premium_plan: "monthly" }), "#C9A86A")}
        {bulkBtn("Ban", "admin-users-bulk-ban", () => doBulk("Ban", { action: "ban", banned: true }), "#c86a6a")}
        {bulkBtn("Unban", "admin-users-bulk-unban", () => doBulk("Unban", { action: "ban", banned: false }), "#c9c9c9")}
      </div>
      <Table
        testid="admin-users-table"
        headers={[
          <input
            key="select-all"
            type="checkbox"
            data-testid="admin-users-select-all"
            checked={allSelected}
            onChange={toggleAll}
          />,
          "Email", "Tier", "Verified", "Joined", "Last Login", "Cols", "Items", "Banned", "Actions",
        ]}
        rows={filtered.map(u => (
          <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
            <td style={cell}>
              <input
                type="checkbox"
                data-testid={`admin-user-select-${u.user_id}`}
                checked={selected.has(u.user_id)}
                onChange={() => toggleOne(u.user_id)}
              />
            </td>
            <td style={cell}>{u.email}</td>
            <td style={cell}><span style={{ color: "#C9A86A" }}>{u.tier}</span></td>
            <td style={cell} data-testid={`admin-user-verified-${u.user_id}`}>{u.email_verified ? "✓" : "✗"}</td>
            <td style={cell} data-testid={`admin-user-joined-${u.user_id}`}>{fmtDate(u.date_joined)}</td>
            <td style={cell} data-testid={`admin-user-lastlogin-${u.user_id}`}>{fmtDate(u.last_login)}</td>
            <td style={cell}>{u.collections_count}</td>
            <td style={cell}>{u.items_count}</td>
            <td style={cell}>{u.banned ? "yes" : "no"}</td>
            <td style={cell}>
              <button data-testid={`admin-user-tier-basic-${u.user_id}`} onClick={() => doAction(u.user_id, () => client.patch(`/users/${u.user_id}/tier`, { tier: "basic" }), "Set Basic")} className="mr-2 text-xs" style={{ color: "#C9A86A" }}>→Basic</button>
              <button data-testid={`admin-user-tier-premium-${u.user_id}`} onClick={() => doAction(u.user_id, () => client.patch(`/users/${u.user_id}/tier`, { tier: "premium", premium_plan: "monthly" }), "Set Premium")} className="mr-2 text-xs" style={{ color: "#C9A86A" }}>→Premium</button>
              <button data-testid={`admin-user-tier-trial-${u.user_id}`} onClick={() => doAction(u.user_id, () => client.patch(`/users/${u.user_id}/tier`, { tier: "trial" }), "Set Trial")} className="mr-2 text-xs" style={{ color: "#c86a6a" }}>→Trial</button>
              <button data-testid={`admin-user-trial-extend-${u.user_id}`} onClick={() => doAction(u.user_id, () => client.patch(`/users/${u.user_id}/trial`, { days: 7 }), "Extended 7d")} className="mr-2 text-xs" style={{ color: "#c9c9c9" }}>+7d</button>
              <button data-testid={`admin-user-impersonate-${u.user_id}`} onClick={() => impersonate(u.user_id, u.email, currentUser?.email)} className="mr-2 text-xs" style={{ color: "#8ab4f8" }}>Impersonate</button>
              {!u.banned && (
                <button
                  data-testid={`admin-user-kick-${u.user_id}`}
                  onClick={async () => {
                    if (!window.confirm(`Force logout all sessions for ${u.email}?`)) return;
                    try {
                      const r = await client.post(`/users/${u.user_id}/kick`);
                      toast.success(`Kicked — ${r.data?.revoked_sessions ?? 0} session(s) revoked`);
                    } catch { toast.error("Kick failed"); }
                  }}
                  className="mr-2 text-xs"
                  style={{ color: "#e0b34a" }}
                >Kick</button>
              )}
              <button
                data-testid={`admin-user-notes-${u.user_id}`}
                onClick={async () => {
                  const current = u.notes || "";
                  const next = window.prompt(`Private admin note for ${u.email} (max 2000 chars):`, current);
                  if (next === null) return; // cancelled
                  try {
                    await client.patch(`/users/${u.user_id}/notes`, { notes: next.slice(0, 2000) });
                    toast.success("Note saved");
                    setTick(t => t + 1);
                  } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || "Note save failed"); }
                }}
                className="mr-2 text-xs"
                title={u.notes ? `Current: ${u.notes}` : "No note"}
                style={{ color: u.notes ? "#C9A86A" : "#c9c9c9" }}
              >{u.notes ? "Note*" : "Note"}</button>
              <button data-testid={`admin-user-ban-${u.user_id}`} onClick={() => { if (!window.confirm(u.banned ? "Unban this user?" : "Ban this user?")) return; doAction(u.user_id, () => client.patch(`/users/${u.user_id}/ban`, { banned: !u.banned }), u.banned ? "Unbanned" : "Banned"); }} className="mr-2 text-xs" style={{ color: "#c86a6a" }}>{u.banned ? "Unban" : "Ban"}</button>
              {!u.banned && (
                <button data-testid={`admin-user-reset-${u.user_id}`} onClick={async () => { if (!window.confirm("Reset password? A temp password will be shown once.")) return; try { const r = await client.post(`/users/${u.user_id}/reset_password`); prompt("Temporary password (copy now):", r.data.temp_password); } catch { toast.error("Reset failed"); } }} className="text-xs" style={{ color: "#c9c9c9" }}>Reset PW</button>
              )}
              {u.banned && (
                <button
                  data-testid={`admin-user-delete-${u.user_id}`}
                  onClick={() => setDeleteTarget(u)}
                  className="text-xs font-semibold"
                  style={{ color: "#ff5c5c" }}
                >Delete User</button>
              )}
            </td>
          </tr>
        ))}
      />
      {deleteTarget && (
        <div
          data-testid="admin-user-delete-modal"
          onClick={() => !deleteBusy && setDeleteTarget(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#1a1a1a", color: "#f0f0f0",
              border: "1px solid #2a2a2a", borderRadius: 8,
              padding: 24, maxWidth: 480, width: "92%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>
              Delete User Account
            </h3>
            <p data-testid="admin-user-delete-modal-email" style={{ fontSize: 13, color: "#C9A86A", marginBottom: 10 }}>
              {deleteTarget.email}
            </p>
            <p style={{ fontSize: 14, lineHeight: 1.55, color: "#c9c9c9", marginBottom: 20 }}>
              This action is permanent and will remove the user, their collections, items, categories, analytics, logs, and Stripe customer. This cannot be undone.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                data-testid="admin-user-delete-cancel"
                disabled={deleteBusy}
                onClick={() => setDeleteTarget(null)}
                style={{
                  padding: "8px 16px", fontSize: 13,
                  background: "transparent", color: "#c9c9c9",
                  border: "1px solid #3a3a3a", borderRadius: 4,
                  cursor: deleteBusy ? "not-allowed" : "pointer",
                }}
              >Cancel</button>
              <button
                data-testid="admin-user-delete-confirm"
                disabled={deleteBusy}
                onClick={async () => {
                  // Snapshot the id up front so a race on `deleteTarget`
                  // (overlay click, parent refetch) can't null the URL
                  // mid-request. Early-out with a user-visible toast if
                  // the modal was somehow opened without a selected row
                  // so the delete request isn't dispatched against
                  // `/users/undefined`.
                  const userId = deleteTarget?.user_id;
                  if (!userId) {
                    toast.error("No user selected");
                    setDeleteTarget(null);
                    return;
                  }
                  setDeleteBusy(true);
                  try {
                    await client.delete(`/users/${userId}`);
                    toast.success("User deleted successfully.");
                    setDeleteTarget(null);
                    setTick(t => t + 1);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail?.error || "Delete failed");
                  } finally {
                    // Always clear the overlay — covers network errors,
                    // 403 refusals, and the TypeError path above.
                    setDeleteBusy(false);
                  }
                }}
                style={{
                  padding: "8px 16px", fontSize: 13, fontWeight: 600,
                  background: "#c0392b", color: "#fff",
                  border: "1px solid #c0392b", borderRadius: 4,
                  cursor: deleteBusy ? "not-allowed" : "pointer",
                  opacity: deleteBusy ? 0.7 : 1,
                }}
              >{deleteBusy ? "Deleting…" : "Confirm Delete"}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// --------- Stats -----------------------------------------------------
function Stats() {
  const { data, loading } = useFetch("/stats/tiers");
  if (loading) return <p>Loading…</p>;
  const s = data || {};
  const stat = (label, val, tid) => (
    <div data-testid={tid} className="p-4 rounded-md" style={{ background: "#1a1a1a", border: "1px solid #2a2a2a" }}>
      <div className="text-xs" style={{ color: "#9a9a9a" }}>{label}</div>
      <div className="text-2xl font-bold" style={{ color: "#C9A86A" }}>{val ?? "—"}</div>
    </div>
  );
  return (
    <>
      <h2 className="text-lg font-semibold mb-4">Tiers &amp; Analytics</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {stat("Total users",         s.total_users,        "stat-total")}
        {stat("Trial current",       s.trial_current,      "stat-trial-current")}
        {stat("Trial expired",       s.trial_expired,      "stat-trial-expired")}
        {stat("Basic",               s.basic_purchasers,   "stat-basic")}
        {stat("Premium active",      s.premium_active,     "stat-premium")}
        {stat("Trial→Basic %",       (s.conversion_trial_to_basic ?? "—") + "%", "stat-conv-basic")}
        {stat("Basic→Premium %",     (s.conversion_basic_to_premium ?? "—") + "%", "stat-conv-premium")}
        {stat("Premium churn %",     (s.premium_churn_rate ?? "—") + "%", "stat-churn")}
      </div>
    </>
  );
}

// --------- Collections ------------------------------------------------
function Cols() {
  const [tick, setTick] = useState(0);
  const { data, loading } = useFetch("/collections", [tick]);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const list = data || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(c => (c.name || "").toLowerCase().includes(needle) || (c.type || "").toLowerCase().includes(needle));
  }, [data, q]);
  if (loading) return <p>Loading…</p>;
  const doDelete = async (id) => {
    if (!window.confirm("Soft-delete this collection?")) return;
    try { await client.delete(`/collections/${id}`); toast.success("Deleted"); setTick(t => t + 1); }
    catch { toast.error("Delete failed"); }
  };
  return (
    <>
      <h2 className="text-lg font-semibold mb-4">Collections ({filtered.length}{q ? ` of ${data?.length || 0}` : ""})</h2>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Search by name or type…" testid="admin-cols-search" />
        <button
          data-testid="admin-cols-export-csv"
          onClick={() => {
            const headers = ["Name", "Type", "Tier Required", "Items", "Owner (short id)", "Created At"];
            const rows = filtered.map(c => [c.name, c.type, c.tier_required, c.items_count, (c.created_by || "").slice(0, 8), c.created_at || ""]);
            const stamp = new Date().toISOString().slice(0, 10);
            downloadCsv(`cdi-collections-${stamp}.csv`, headers, rows);
            toast.success(`Exported ${rows.length} collection(s) to CSV`);
          }}
          disabled={filtered.length === 0}
          className="px-3 py-2 text-xs rounded-md mb-3"
          style={{
            background: filtered.length === 0 ? "#1a1a1a" : "#231d10",
            color: filtered.length === 0 ? "#6a6a6a" : "#C9A86A",
            border: `1px solid ${filtered.length === 0 ? "#2a2a2a" : "#C9A86A"}`,
            cursor: filtered.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Download CSV ({filtered.length})
        </button>
      </div>
      <Table
        testid="admin-cols-table"
        headers={["Name", "Type", "Tier req.", "Items", "Owner", "Actions"]}
        rows={filtered.map(c => (
          <tr key={c.collection_id} data-testid={`admin-col-row-${c.collection_id}`}>
            <td style={cell}>{c.name}</td>
            <td style={cell}>{c.type}</td>
            <td style={cell}><span style={{ color: c.tier_required === "premium" ? "#C9A86A" : "#c9c9c9" }}>{c.tier_required}</span></td>
            <td style={cell}>{c.items_count}</td>
            <td style={cell} className="text-xs">{(c.created_by || "").slice(0, 8)}</td>
            <td style={cell}>
              <button data-testid={`admin-col-delete-${c.collection_id}`} onClick={() => doDelete(c.collection_id)} className="text-xs" style={{ color: "#c86a6a" }}>Delete</button>
            </td>
          </tr>
        ))}
      />
    </>
  );
}

// --------- Items ------------------------------------------------------
function Items() {
  const [tick, setTick] = useState(0);
  const { data, loading } = useFetch("/items", [tick]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const filtered = useMemo(() => {
    const list = data || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(i =>
      (i.name || "").toLowerCase().includes(needle) ||
      (i.collection_name || "").toLowerCase().includes(needle)
    );
  }, [data, q]);

  const allSelected = filtered.length > 0 && filtered.every(i => selected.has(i.item_id));
  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) filtered.forEach(i => next.delete(i.item_id));
      else filtered.forEach(i => next.add(i.item_id));
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (loading) return <p>Loading…</p>;

  const action = async (id, fn, msg) => {
    try { await fn(); toast.success(msg); setTick(t => t + 1); }
    catch { toast.error("Action failed"); }
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Soft-delete ${ids.length} item(s)? This cannot be undone from the UI.`)) return;
    setBulkBusy(true);
    try {
      const r = await client.post("/items/bulk_delete", { ids });
      toast.success(`Deleted ${r.data?.deleted ?? ids.length} of ${ids.length}`);
      setSelected(new Set());
      setTick(t => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <>
      <h2 className="text-lg font-semibold mb-4">Items ({filtered.length}{q ? ` of ${data?.length || 0}` : ""})</h2>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <SearchInput value={q} onChange={setQ} placeholder="Search by name or collection…" testid="admin-items-search" />
        <button
          data-testid="admin-items-export-csv"
          onClick={() => {
            const headers = ["Name", "Collection", "Tier Required", "Flags", "Created At"];
            const rows = filtered.map(i => [i.name, i.collection_name || "", i.tier_required, (i.flags || []).join("|"), i.created_at || ""]);
            const stamp = new Date().toISOString().slice(0, 10);
            downloadCsv(`cdi-items-${stamp}.csv`, headers, rows);
            toast.success(`Exported ${rows.length} item(s) to CSV`);
          }}
          disabled={filtered.length === 0}
          className="px-3 py-2 text-xs rounded-md mb-3"
          style={{
            background: filtered.length === 0 ? "#1a1a1a" : "#231d10",
            color: filtered.length === 0 ? "#6a6a6a" : "#C9A86A",
            border: `1px solid ${filtered.length === 0 ? "#2a2a2a" : "#C9A86A"}`,
            cursor: filtered.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Download CSV ({filtered.length})
        </button>
        <button
          data-testid="admin-items-bulk-delete"
          onClick={doBulkDelete}
          disabled={selected.size === 0 || bulkBusy}
          className="px-3 py-2 text-xs rounded-md mb-3"
          style={{
            background: selected.size === 0 ? "#1a1a1a" : "#3a1414",
            color: selected.size === 0 ? "#6a6a6a" : "#ff9a9a",
            border: `1px solid ${selected.size === 0 ? "#2a2a2a" : "#6a2020"}`,
            cursor: selected.size === 0 ? "not-allowed" : "pointer",
          }}
        >
          {bulkBusy ? "Deleting…" : `Delete Selected (${selected.size})`}
        </button>
      </div>
      <Table
        testid="admin-items-table"
        headers={[
          <input
            key="select-all"
            type="checkbox"
            data-testid="admin-items-select-all"
            checked={allSelected}
            onChange={toggleAll}
          />,
          "Name",
          "Collection",
          "Tier req.",
          "Flags",
          "Actions",
        ]}
        rows={filtered.map(i => (
          <tr key={i.item_id} data-testid={`admin-item-row-${i.item_id}`}>
            <td style={cell}>
              <input
                type="checkbox"
                data-testid={`admin-item-select-${i.item_id}`}
                checked={selected.has(i.item_id)}
                onChange={() => toggleOne(i.item_id)}
              />
            </td>
            <td style={cell}>{i.name}</td>
            <td style={cell}>{i.collection_name || "—"}</td>
            <td style={cell}>{i.tier_required}</td>
            <td style={cell}>{(i.flags || []).join(", ") || "—"}</td>
            <td style={cell}>
              <button data-testid={`admin-item-flag-${i.item_id}`} onClick={() => action(i.item_id, () => client.patch(`/items/${i.item_id}/flag`, { flag: "review", add: !(i.flags || []).includes("review") }), "Flag toggled")} className="mr-2 text-xs" style={{ color: "#e0b34a" }}>Flag</button>
              <button data-testid={`admin-item-delete-${i.item_id}`} onClick={() => { if (!window.confirm("Soft-delete this item?")) return; action(i.item_id, () => client.delete(`/items/${i.item_id}`), "Deleted"); }} className="text-xs" style={{ color: "#c86a6a" }}>Delete</button>
            </td>
          </tr>
        ))}
      />
    </>
  );
}

// --------- Logs -------------------------------------------------------
function Logs() {
  const [qAdmin, setQAdmin] = useState("");
  const [qAction, setQAction] = useState("");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Server-side filter query. Debounced by 300ms on text inputs so we
  // don't hammer the API on every keystroke.
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      const params = new URLSearchParams();
      params.set("limit", "500");
      if (qAdmin.trim()) params.set("admin_email", qAdmin.trim());
      if (qAction.trim()) params.set("action", qAction.trim());
      if (dFrom) params.set("date_from", dFrom);
      if (dTo) params.set("date_to", dTo);
      try {
        const r = await client.get(`/logs?${params.toString()}`);
        if (!cancelled) setData(r.data);
      } catch {
        if (!cancelled) setData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [qAdmin, qAction, dFrom, dTo, tick]);

  const clearFilters = () => { setQAdmin(""); setQAction(""); setDFrom(""); setDTo(""); };

  const dateInputStyle = { background: "#1a1a1a", border: "1px solid #2a2a2a", color: "#f7f7f7", padding: "8px 10px", borderRadius: 6, fontSize: 12 };
  const rows = data || [];
  return (
    <>
      <h2 className="text-lg font-semibold mb-2">Audit Logs ({loading ? "…" : rows.length})</h2>
      <div className="flex items-end gap-3 mb-3 flex-wrap">
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "#9a9a9a" }}>Admin email</label>
          <input
            data-testid="admin-logs-filter-admin"
            value={qAdmin}
            onChange={(e) => setQAdmin(e.target.value)}
            placeholder="e.g. vince@…"
            className="px-3 py-2 text-sm rounded-md"
            style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", color: "#f7f7f7", width: 200 }}
          />
        </div>
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "#9a9a9a" }}>Action</label>
          <input
            data-testid="admin-logs-filter-action"
            value={qAction}
            onChange={(e) => setQAction(e.target.value)}
            placeholder="e.g. user_ban"
            className="px-3 py-2 text-sm rounded-md"
            style={{ background: "#1a1a1a", border: "1px solid #2a2a2a", color: "#f7f7f7", width: 180 }}
          />
        </div>
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "#9a9a9a" }}>From</label>
          <input type="date" data-testid="admin-logs-filter-from" value={dFrom} onChange={(e) => setDFrom(e.target.value)} style={dateInputStyle} />
        </div>
        <div>
          <label className="block text-[10px] mb-1" style={{ color: "#9a9a9a" }}>To</label>
          <input type="date" data-testid="admin-logs-filter-to" value={dTo} onChange={(e) => setDTo(e.target.value)} style={dateInputStyle} />
        </div>
        <button
          data-testid="admin-logs-filter-clear"
          onClick={clearFilters}
          className="px-3 py-2 text-xs rounded-md"
          style={{ background: "#1a1a1a", color: "#c9c9c9", border: "1px solid #2a2a2a", height: 36 }}
        >
          Clear
        </button>
        <button
          data-testid="admin-logs-refresh"
          onClick={() => setTick(t => t + 1)}
          className="px-3 py-2 text-xs rounded-md"
          style={{ background: "#231d10", color: "#C9A86A", border: "1px solid #C9A86A", height: 36 }}
        >
          Refresh
        </button>
        <button
          data-testid="admin-logs-export-csv"
          onClick={() => {
            const headers = ["Timestamp", "Admin", "Action", "Target Type", "Target Id", "Result", "IP"];
            const csvRows = rows.map(l => [l.timestamp || "", l.admin_email || "", l.action || "", l.target_type || "", l.target_id || "", l.result || "", l.ip || ""]);
            const stamp = new Date().toISOString().slice(0, 10);
            downloadCsv(`cdi-audit-logs-${stamp}.csv`, headers, csvRows);
            toast.success(`Exported ${csvRows.length} log row(s) to CSV`);
          }}
          disabled={rows.length === 0}
          className="px-3 py-2 text-xs rounded-md"
          style={{
            background: rows.length === 0 ? "#1a1a1a" : "#231d10",
            color: rows.length === 0 ? "#6a6a6a" : "#C9A86A",
            border: `1px solid ${rows.length === 0 ? "#2a2a2a" : "#C9A86A"}`,
            height: 36,
            cursor: rows.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Download CSV ({rows.length})
        </button>
      </div>
      <Table
        testid="admin-logs-table"
        headers={["Timestamp", "Admin", "Action", "Target", "Result"]}
        rows={rows.map((l, idx) => (
          <tr key={idx} data-testid={`admin-log-row-${idx}`}>
            <td style={cell} className="text-xs">{l.timestamp}</td>
            <td style={cell}>{l.admin_email || "—"}</td>
            <td style={cell}>{l.action}</td>
            <td style={cell} className="text-xs">{l.target_type}/{(l.target_id || "").slice(0, 8)}</td>
            <td style={cell}>{l.result || "—"}</td>
          </tr>
        ))}
      />
    </>
  );
}

// --------- System Audit Log (read-only) -------------------------------
// Paginated table backed by the `admin_write_audit` Mongo collection
// (server-side sort by timestamp desc, skip/limit pagination). Combines
// two sources: `admin_api_middleware` (every /api/admin/* request) and
// `agent_cli` (agent-side file / migration / deploy events). Read-only
// viewer — no actions, no mutations, per user directive.
function AuditWrite() {
  const [skip, setSkip] = useState(0);
  // Mutually-exclusive filter: null | "agent" | "live" | "unexpected".
  // Click a pill to activate; click the same pill again to deactivate.
  const [filter, setFilter] = useState(null);
  const LIMIT = 50;
  const [tick, setTick] = useState(0);
  const [payload, setPayload] = useState({ total: 0, rows: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams({ skip: String(skip), limit: String(LIMIT) });
    if (filter === "agent")      qs.set("agent_only", "1");
    if (filter === "live")       qs.set("live_only", "1");
    if (filter === "unexpected") qs.set("unexpected_only", "1");
    client.get(`/write-audit?${qs.toString()}`)
      .then((r) => { if (!cancelled) setPayload(r.data || { total: 0, rows: [] }); })
      .catch(() => { if (!cancelled) setPayload({ total: 0, rows: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [skip, tick, filter]);

  // Reset paging whenever the active filter changes so the user always
  // starts on page 1 of the newly filtered result set.
  const activateFilter = (id) => {
    setFilter((cur) => (cur === id ? null : id));
    setSkip(0);
  };

  const { total, rows } = payload;
  const page = Math.floor(skip / LIMIT) + 1;
  const totalPages = Math.max(1, Math.ceil((total || 0) / LIMIT));

  const resultPill = (r) => {
    const map = {
      executed: { bg: "#193a19", fg: "#7ab97a" },
      blocked:  { bg: "#3a1919", fg: "#c97a7a" },
      failed:   { bg: "#3a2f19", fg: "#c9a86a" },
    };
    const s = map[r] || { bg: "#1a1a1a", fg: "#c9c9c9" };
    return (
      <span style={{ padding: "2px 6px", borderRadius: 3, fontSize: 10, background: s.bg, color: s.fg }}>
        {r || "—"}
      </span>
    );
  };
  const sourcePill = (src) => (
    <span style={{
      padding: "2px 6px", borderRadius: 3, fontSize: 10,
      background: src === "agent_cli" ? "#231d10" : "#1a1a1a",
      color: src === "agent_cli" ? "#C9A86A" : "#9a9a9a",
      border: "1px solid " + (src === "agent_cli" ? "#C9A86A" : "#2a2a2a"),
    }}>{src || "—"}</span>
  );

  return (
    <>
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <h2 className="text-lg font-semibold">System Audit Log</h2>
          <p className="text-xs mt-1" style={{ color: "#9a9a9a", maxWidth: 720 }}>
            Read-only trail from <code style={{ color: "#C9A86A" }}>admin_write_audit</code>.
            Combines every admin-API request (via middleware) with agent-side
            file / migration / deploy events (via CLI). Sorted by timestamp descending.
          </p>
        </div>
        <button
          data-testid="admin-audit-refresh"
          onClick={() => setTick((t) => t + 1)}
          className="px-3 py-2 text-xs rounded-md shrink-0"
          style={{ background: "#231d10", color: "#C9A86A", border: "1px solid #C9A86A", height: 36 }}
        >
          Refresh
        </button>
      </div>
      {/* Filter toggles. Click again to deselect. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="text-[10px] uppercase tracking-wider" style={{ color: "#9a9a9a" }}>Filter:</span>
        {[
          { id: "agent",      label: "Agent-Only" },
          { id: "live",       label: "Live-Only" },
          { id: "unexpected", label: "Unexpected Changes" },
        ].map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              data-testid={`admin-audit-filter-${f.id}`}
              aria-pressed={active}
              onClick={() => activateFilter(f.id)}
              className="px-3 py-1.5 text-xs rounded-full transition-colors"
              style={{
                background: active ? "#231d10" : "#1a1a1a",
                color:      active ? "#C9A86A" : "#c9c9c9",
                border:     `1px solid ${active ? "#C9A86A" : "#2a2a2a"}`,
              }}
            >
              {f.label}
            </button>
          );
        })}
        {filter && (
          <button
            data-testid="admin-audit-filter-clear"
            onClick={() => { setFilter(null); setSkip(0); }}
            className="text-xs underline ml-1"
            style={{ color: "#9a9a9a" }}
          >
            Clear
          </button>
        )}
      </div>
      {loading ? (
        <p data-testid="admin-audit-loading">Loading…</p>
      ) : (
        <>
          <Table
            testid="admin-audit-table"
            headers={["Timestamp (UTC)", "Source", "User", "Method", "Route / File", "Status", "Result", "Env", "Payload"]}
            rows={
              rows.length === 0 ? (
                <tr><td style={{ ...cell, textAlign: "center", color: "#9a9a9a" }} colSpan={9}>No audit rows yet</td></tr>
              ) : rows.map((r, idx) => (
                <tr key={`${r.timestamp}-${idx}`} data-testid={`admin-audit-row-${idx}`}>
                  <td style={cell} className="text-xs" title={r.timestamp}>{(r.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                  <td style={cell}>
                    {sourcePill(r.source)}
                    {r.unrequested && (
                      <span
                        data-testid={`admin-audit-row-${idx}-unrequested`}
                        className="ml-1"
                        style={{
                          padding: "2px 6px", borderRadius: 3, fontSize: 10,
                          background: "#3a1919", color: "#c97a7a",
                          border: "1px solid #c97a7a",
                        }}
                        title="No admin session activity within ±2 minutes"
                      >Unrequested</span>
                    )}
                  </td>
                  <td style={cell} className="text-xs">{r.user || "—"}</td>
                  <td style={cell} className="text-xs">{r.method || "—"}</td>
                  <td style={cell} className="text-xs" title={r.route}>{(r.route || "").length > 60 ? (r.route || "").slice(0, 57) + "…" : (r.route || "—")}</td>
                  <td style={cell} className="text-xs">{r.status_code == null ? "—" : r.status_code}</td>
                  <td style={cell}>{resultPill(r.result)}</td>
                  <td style={cell} className="text-xs">{r.environment || "—"}</td>
                  <td style={cell} className="text-xs" title={r.payload || ""}>
                    {(r.payload || "").length > 60 ? (r.payload || "").slice(0, 57) + "…" : (r.payload || "—")}
                  </td>
                </tr>
              ))
            }
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs" data-testid="admin-audit-pageinfo" style={{ color: "#9a9a9a" }}>
              Page {page} of {totalPages} · {total} row{total === 1 ? "" : "s"} total · showing {rows.length}
            </span>
            <div className="flex gap-2">
              <button
                data-testid="admin-audit-prev"
                onClick={() => setSkip(Math.max(0, skip - LIMIT))}
                disabled={skip === 0}
                className="px-3 py-2 text-xs rounded-md"
                style={{
                  background: skip === 0 ? "#1a1a1a" : "#231d10",
                  color: skip === 0 ? "#6a6a6a" : "#C9A86A",
                  border: `1px solid ${skip === 0 ? "#2a2a2a" : "#C9A86A"}`,
                  cursor: skip === 0 ? "not-allowed" : "pointer",
                }}
              >Prev</button>
              <button
                data-testid="admin-audit-next"
                onClick={() => setSkip(skip + LIMIT)}
                disabled={skip + LIMIT >= total}
                className="px-3 py-2 text-xs rounded-md"
                style={{
                  background: (skip + LIMIT >= total) ? "#1a1a1a" : "#231d10",
                  color: (skip + LIMIT >= total) ? "#6a6a6a" : "#C9A86A",
                  border: `1px solid ${(skip + LIMIT >= total) ? "#2a2a2a" : "#C9A86A"}`,
                  cursor: (skip + LIMIT >= total) ? "not-allowed" : "pointer",
                }}
              >Next</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}


// --------- Regression Monitor (read-only) -----------------------------
// Reads the `regression_checks` collection via GET /admin/regression-checks.
// Row click opens a drawer with the full check list. Header banner
// appears whenever recent_issues_24h > 0 with a jump link to the latest
// failing run.
function RegressionMonitor() {
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;
  const [tick, setTick] = useState(0);
  const [envFilter, setEnvFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [payload, setPayload] = useState({ total: 0, rows: [], recent_issues_24h: 0 });
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams({ skip: String(skip), limit: String(LIMIT) });
    if (envFilter)    qs.set("environment", envFilter);
    if (statusFilter) qs.set("status", statusFilter);
    client.get(`/regression-checks?${qs.toString()}`)
      .then((r) => { if (!cancelled) setPayload(r.data || { total: 0, rows: [] }); })
      .catch(() => { if (!cancelled) setPayload({ total: 0, rows: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [skip, tick, envFilter, statusFilter]);

  const { total, rows, recent_issues_24h } = payload;
  const page = Math.floor(skip / LIMIT) + 1;
  const totalPages = Math.max(1, Math.ceil((total || 0) / LIMIT));

  const statusPill = (s) => (
    <span style={{
      padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600,
      background: s === "ok" ? "#193a19" : "#3a1919",
      color: s === "ok" ? "#7ab97a" : "#c97a7a",
      border: `1px solid ${s === "ok" ? "#7ab97a" : "#c97a7a"}`,
    }}>{s === "ok" ? "OK" : "Issues found"}</span>
  );

  const latestFailing = rows.find((r) => r.status === "issues_found");

  return (
    <>
      <div className="mb-3">
        <h2 className="text-lg font-semibold">Regression Monitor</h2>
        <p className="text-xs mt-1" style={{ color: "#9a9a9a", maxWidth: 780 }}>
          Non-destructive checks run automatically after every agent CLI event and
          every write-verb admin API request. &quot;Issues found&quot; means at least one of
          categories, presets, templates, publish pipeline, schema, endpoints,
          preview / live isolation, or migrations returned a failure. Each entry
          links back to the System Audit row that triggered it.
        </p>
      </div>
      {recent_issues_24h > 0 && (
        <div
          data-testid="admin-reg-banner"
          className="flex items-center justify-between mb-3 px-3 py-2 rounded-md"
          style={{ background: "#3a1919", border: "1px solid #c97a7a", color: "#f7cfcf" }}
        >
          <span className="text-xs">
            <b>{recent_issues_24h}</b> issue(s) detected in the last 24 hours.
          </span>
          {latestFailing && (
            <button
              data-testid="admin-reg-banner-open"
              onClick={() => setDrawer(latestFailing)}
              className="text-xs underline"
              style={{ color: "#f7cfcf" }}
            >Open most recent</button>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 mb-3">
        <select
          data-testid="admin-reg-filter-env"
          value={envFilter}
          onChange={(e) => { setEnvFilter(e.target.value); setSkip(0); }}
          className="px-2 py-1.5 text-xs rounded-md"
          style={{ background: "#1a1a1a", color: "#c9c9c9", border: "1px solid #2a2a2a" }}
        >
          <option value="">All envs</option>
          <option value="preview">Preview</option>
          <option value="live">Live</option>
          <option value="unknown">Unknown</option>
        </select>
        <select
          data-testid="admin-reg-filter-status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
          className="px-2 py-1.5 text-xs rounded-md"
          style={{ background: "#1a1a1a", color: "#c9c9c9", border: "1px solid #2a2a2a" }}
        >
          <option value="">All statuses</option>
          <option value="ok">OK</option>
          <option value="issues_found">Issues found</option>
        </select>
        <button
          data-testid="admin-reg-refresh"
          onClick={() => setTick((t) => t + 1)}
          className="px-3 py-1.5 text-xs rounded-md"
          style={{ background: "#231d10", color: "#C9A86A", border: "1px solid #C9A86A" }}
        >
          Refresh
        </button>
        <span className="text-xs ml-auto" style={{ color: "#9a9a9a" }}>
          {total} record{total === 1 ? "" : "s"}
        </span>
      </div>
      {loading ? <p data-testid="admin-reg-loading">Loading…</p> : (
        <>
          <Table
            testid="admin-reg-table"
            headers={["Timestamp (UTC)", "Env", "Status", "Summary", "Triggered By", "Details"]}
            rows={
              rows.length === 0 ? (
                <tr><td style={{ ...cell, textAlign: "center", color: "#9a9a9a" }} colSpan={6}>No regression runs yet</td></tr>
              ) : rows.map((r, idx) => (
                <tr key={r.id || idx} data-testid={`admin-reg-row-${idx}`}>
                  <td style={cell} className="text-xs" title={r.timestamp}>{(r.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                  <td style={cell} className="text-xs">{r.environment || "—"}</td>
                  <td style={cell}>{statusPill(r.status)}</td>
                  <td style={cell} className="text-xs" title={r.summary}>{(r.summary || "").length > 70 ? (r.summary || "").slice(0, 67) + "…" : (r.summary || "—")}</td>
                  <td style={cell} className="text-xs" title={r.triggered_by_audit_id || ""}>
                    {r.triggered_by_audit_id ? String(r.triggered_by_audit_id).slice(0, 10) + "…" : "—"}
                  </td>
                  <td style={cell}>
                    <button
                      data-testid={`admin-reg-details-${idx}`}
                      onClick={() => setDrawer(r)}
                      className="px-2 py-1 text-xs rounded-md"
                      style={{ background: "#1a1a1a", color: "#C9A86A", border: "1px solid #2a2a2a" }}
                    >Details</button>
                  </td>
                </tr>
              ))
            }
          />
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs" data-testid="admin-reg-pageinfo" style={{ color: "#9a9a9a" }}>
              Page {page} of {totalPages} · showing {rows.length}
            </span>
            <div className="flex gap-2">
              <button
                data-testid="admin-reg-prev"
                onClick={() => setSkip(Math.max(0, skip - LIMIT))}
                disabled={skip === 0}
                className="px-3 py-2 text-xs rounded-md"
                style={{
                  background: skip === 0 ? "#1a1a1a" : "#231d10",
                  color: skip === 0 ? "#6a6a6a" : "#C9A86A",
                  border: `1px solid ${skip === 0 ? "#2a2a2a" : "#C9A86A"}`,
                  cursor: skip === 0 ? "not-allowed" : "pointer",
                }}
              >Prev</button>
              <button
                data-testid="admin-reg-next"
                onClick={() => setSkip(skip + LIMIT)}
                disabled={skip + LIMIT >= total}
                className="px-3 py-2 text-xs rounded-md"
                style={{
                  background: (skip + LIMIT >= total) ? "#1a1a1a" : "#231d10",
                  color: (skip + LIMIT >= total) ? "#6a6a6a" : "#C9A86A",
                  border: `1px solid ${(skip + LIMIT >= total) ? "#2a2a2a" : "#C9A86A"}`,
                  cursor: (skip + LIMIT >= total) ? "not-allowed" : "pointer",
                }}
              >Next</button>
            </div>
          </div>
        </>
      )}
      {drawer && (
        <div
          data-testid="admin-reg-drawer"
          className="fixed inset-0 z-40 flex justify-end"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={(e) => { if (e.target === e.currentTarget) setDrawer(null); }}
        >
          <div className="h-full w-full max-w-xl overflow-auto p-6" style={{ background: "#141414", borderLeft: "1px solid #2a2a2a" }}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold">Regression run</h3>
                <p className="text-xs mt-1" style={{ color: "#9a9a9a" }}>
                  {drawer.timestamp} · env <b>{drawer.environment}</b>
                </p>
              </div>
              <button
                data-testid="admin-reg-drawer-close"
                onClick={() => setDrawer(null)}
                className="text-xs"
                style={{ color: "#C9A86A" }}
              >Close ✕</button>
            </div>
            <div className="mb-4">
              {statusPill(drawer.status)}
              <p className="text-xs mt-2" style={{ color: "#c9c9c9" }}>{drawer.summary}</p>
            </div>
            <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "#9a9a9a" }}>
              Triggered by audit id
            </div>
            <div className="text-xs mb-4" style={{ color: "#C9A86A", fontFamily: "monospace" }}>
              {drawer.triggered_by_audit_id || "—"}
            </div>
            <div className="text-[10px] uppercase tracking-wider mb-2" style={{ color: "#9a9a9a" }}>Checks</div>
            <div className="space-y-2">
              {(drawer.checks || []).map((c, i) => (
                <div
                  key={i}
                  data-testid={`admin-reg-drawer-check-${c.name}`}
                  className="p-3 rounded-md"
                  style={{
                    background: "#1a1a1a",
                    border: `1px solid ${c.passed ? "#2a3a2a" : "#3a2a2a"}`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{ color: c.passed ? "#7ab97a" : "#c97a7a" }}>
                      {c.passed ? "✓" : "✗"} {c.name}
                    </span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: "#c9c9c9" }}>{c.details}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// --------- Health -----------------------------------------------------
function Health() {
  const { data, loading } = useFetch("/system/health");
  if (loading) return <p>Loading…</p>;
  const h = data || {};
  const row = (k, v, tid) => (
    <div data-testid={tid} className="flex justify-between p-3" style={{ borderBottom: "1px solid #2a2a2a" }}>
      <span style={{ color: "#9a9a9a" }}>{k}</span>
      <span style={{ color: (v === "ok" || v === "live") ? "#7ab97a" : "#c9c9c9" }}>{String(v ?? "—")}</span>
    </div>
  );
  return (
    <>
      <h2 className="text-lg font-semibold mb-4">System Health</h2>
      <div className="rounded-md" style={{ background: "#1a1a1a", border: "1px solid #2a2a2a" }}>
        {row("API",              h.api_status,          "health-api")}
        {row("Database",         h.db_status,           "health-db")}
        {row("Email provider",   h.email_status,        "health-email")}
        {row("Stripe",           h.stripe_status,       "health-stripe")}
        {row("Last deployment",  h.last_deployment_at,  "health-deployment")}
        {row("Error rate (24h)", h.error_rate_24h,      "health-errors")}
      </div>
    </>
  );
}


// --------- Data Health (read-only) -----------------------------------
// Renders the output of GET /api/admin/data-health. Runs the same
// Item model_validator that fires on every read from the /items API
// endpoints and surfaces every "Data Health:" warning it produced,
// grouped by item. Read-only — no writes to Mongo.
function DataHealth() {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    client.get("/data-health")
      .then((r) => { if (!cancelled) { setPayload(r.data); setErr(null); } })
      .catch((e) => { if (!cancelled) setErr(e?.response?.status || "err"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tick]);

  const totals = payload || { total_items_scanned: 0, total_warnings: 0, warnings: [] };
  const warnings = Array.isArray(totals.warnings) ? totals.warnings : [];

  // eslint-disable-next-line react/no-unstable-nested-components
  const Stat = ({ label, value, tid }) => (
    <div
      data-testid={tid}
      className="p-4 rounded-md"
      style={{ background: "#1a1a1a", border: "1px solid #2a2a2a" }}
    >
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "#9a9a9a" }}>{label}</div>
      <div className="text-2xl font-semibold" style={{ color: "#C9A86A" }}>{value}</div>
    </div>
  );

  return (
    <div data-testid="admin-data-health">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Data Health</h2>
        <button
          data-testid="admin-data-health-refresh"
          onClick={() => setTick((t) => t + 1)}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-md"
          style={{
            background: loading ? "#2a2a2a" : "#231d10",
            color: "#C9A86A",
            border: "1px solid #C9A86A",
            cursor: loading ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Scanning…" : "Refresh Scan"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <Stat label="Items Scanned" value={loading ? "—" : totals.total_items_scanned} tid="admin-data-health-scanned" />
        <Stat label="Total Warnings" value={loading ? "—" : totals.total_warnings} tid="admin-data-health-total-warnings" />
      </div>

      {err && (
        <div data-testid="admin-data-health-error" className="p-3 rounded-md text-xs" style={{ background: "#2a1414", color: "#e0a2a2", border: "1px solid #6b2828" }}>
          Failed to load data health scan (status {String(err)}).
        </div>
      )}

      {!err && loading && (
        <div data-testid="admin-data-health-loading" className="p-4 text-xs" style={{ color: "#9a9a9a" }}>
          Scanning items…
        </div>
      )}

      {!err && !loading && warnings.length === 0 && (
        <div data-testid="admin-data-health-empty" className="p-4 rounded-md text-xs" style={{ background: "#122016", color: "#7ab97a", border: "1px solid #295a3a" }}>
          No data health warnings detected across {totals.total_items_scanned} items.
        </div>
      )}

      {!err && !loading && warnings.length > 0 && (
        <div className="overflow-auto" style={{ border: "1px solid #2a2a2a", borderRadius: 6 }}>
          <table data-testid="admin-data-health-table" className="w-full">
            <thead>
              <tr>
                <th style={th}>Item ID</th>
                <th style={th}>Warnings</th>
              </tr>
            </thead>
            <tbody>
              {warnings.map((row) => (
                <tr key={row.item_id} data-testid={`admin-data-health-row-${row.item_id}`}>
                  <td style={{ ...cell, verticalAlign: "top", fontFamily: "monospace", color: "#c9c9c9" }}>
                    {row.item_id || "—"}
                  </td>
                  <td style={{ ...cell, verticalAlign: "top" }}>
                    <ul className="list-disc pl-4 space-y-1">
                      {(row.warnings || []).map((w, i) => (
                        <li
                          key={`${row.item_id}-${i}`}
                          data-testid={`admin-data-health-warning-${row.item_id}-${i}`}
                          style={{ color: "#e6c98c" }}
                        >
                          {String(w)}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// --------- Impersonation Audit ---------------------------------------
// Chronological list of every `user_impersonate` audit row. Now that the
// banner emits a matching `user_return_from_impersonation` row on click,
// duration comes straight from the return row's `duration_seconds=<n>`
// detail — falling back to the next-event heuristic only when no return
// row exists yet (legacy events or admin closed the tab without clicking
// "Return to admin").
function ImpersonationAudit() {
  // Pull BOTH sides of the flow: impersonate + return. limit=500 is
  // enough for any realistic 90-day window.
  const [tick, setTick] = useState(0);
  const [rows, setRows] = useState(null);
  const [returns, setReturns] = useState(null);
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      client.get("/logs?action=user_impersonate&limit=500"),
      client.get("/logs?action=user_return_from_impersonation&limit=500"),
    ]).then(([a, b]) => {
      if (!cancelled) { setRows(a.data || []); setReturns(b.data || []); }
    }).catch(() => { if (!cancelled) { setRows([]); setReturns([]); } });
    return () => { cancelled = true; };
  }, [tick]);
  if (rows === null || returns === null) return <p>Loading…</p>;

  // Parse "duration_seconds=N" out of each return event's result field
  // and key by admin_email + target_id to match back to the source event.
  const parseDur = (result) => {
    const m = /duration_seconds=(\d+)/.exec(result || "");
    return m ? Number(m[1]) : null;
  };
  const returnByKey = new Map();
  for (const r of returns) {
    const key = `${r.admin_email || ""}::${r.target_id || ""}::${r.timestamp}`;
    returnByKey.set(key, parseDur(r.result));
  }
  // For each impersonate row, find the earliest return row from the same
  // admin/target AFTER its timestamp — that's the matching return.
  const matchReturn = (imp) => {
    const candidates = returns
      .filter(r => (r.admin_email || "") === (imp.admin_email || "") && (r.target_id || "") === (imp.target_id || "") && new Date(r.timestamp) > new Date(imp.timestamp))
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return candidates[0] || null;
  };

  const fmtSecs = (secs) => {
    if (secs == null || isNaN(secs)) return "—";
    const s = Math.max(0, Math.floor(secs));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const r = s % 60;
    if (h > 0) return `${h}h ${m}m ${r}s`;
    if (m > 0) return `${m}m ${r}s`;
    return `${r}s`;
  };

  // Fall-back heuristic (same as before) for legacy events with no return.
  const sortedAsc = [...rows].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const nextByAdmin = new Map();
  for (let i = sortedAsc.length - 1; i >= 0; i--) {
    const r = sortedAsc[i];
    const key = r.admin_email || "";
    if (nextByAdmin.has(key)) r._next_ts = nextByAdmin.get(key);
    nextByAdmin.set(key, r.timestamp);
  }
  const resolveDur = (imp) => {
    const ret = matchReturn(imp);
    if (ret) {
      const secs = parseDur(ret.result);
      if (secs != null) return { secs, source: "return" };
    }
    if (imp._next_ts) {
      const secs = Math.floor((new Date(imp._next_ts) - new Date(imp.timestamp)) / 1000);
      return { secs, source: "heuristic" };
    }
    return { secs: null, source: null };
  };

  const display = [...sortedAsc].reverse();

  const exportCsv = () => {
    const headers = ["Timestamp", "Admin", "Target Email", "Target User Id", "Duration", "Duration Source", "IP"];
    const csvRows = display.map(r => {
      const d = resolveDur(r);
      return [
        r.timestamp || "",
        r.admin_email || "",
        (r.result || "").replace(/^email=/, ""),
        r.target_id || "",
        fmtSecs(d.secs),
        d.source || "",
        r.ip || "",
      ];
    });
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`cdi-impersonation-audit-${stamp}.csv`, headers, csvRows);
    toast.success(`Exported ${csvRows.length} impersonation event(s) to CSV`);
  };

  return (
    <>
      <h2 className="text-lg font-semibold mb-2">Impersonation Audit ({display.length})</h2>
      <p className="text-xs mb-4" style={{ color: "#9a9a9a" }}>
        Duration is measured exactly when the admin clicks &quot;Return to admin&quot; (source: return); otherwise falls back to the gap until this admin&apos;s next impersonation event (source: heuristic).
      </p>
      <div className="mb-3 flex gap-2 flex-wrap">
        <button
          data-testid="admin-imp-export-csv"
          onClick={exportCsv}
          disabled={display.length === 0}
          className="px-3 py-2 text-xs rounded-md"
          style={{
            background: display.length === 0 ? "#1a1a1a" : "#231d10",
            color: display.length === 0 ? "#6a6a6a" : "#C9A86A",
            border: `1px solid ${display.length === 0 ? "#2a2a2a" : "#C9A86A"}`,
            cursor: display.length === 0 ? "not-allowed" : "pointer",
          }}
        >
          Download CSV ({display.length})
        </button>
        <button
          data-testid="admin-imp-refresh"
          onClick={() => setTick(t => t + 1)}
          className="px-3 py-2 text-xs rounded-md"
          style={{ background: "#1a1a1a", color: "#c9c9c9", border: "1px solid #2a2a2a" }}
        >
          Refresh
        </button>
      </div>
      <Table
        testid="admin-imp-table"
        headers={["Timestamp", "Admin", "Target Email", "Target User Id", "Duration", "Source"]}
        rows={display.map((r, idx) => {
          const d = resolveDur(r);
          return (
            <tr key={idx} data-testid={`admin-imp-row-${idx}`}>
              <td style={cell} className="text-xs">{r.timestamp}</td>
              <td style={cell}>{r.admin_email || "—"}</td>
              <td style={cell}>{(r.result || "").replace(/^email=/, "") || "—"}</td>
              <td style={cell} className="text-xs">{(r.target_id || "").slice(0, 8)}</td>
              <td style={cell} data-testid={`admin-imp-duration-${idx}`}>{fmtSecs(d.secs)}</td>
              <td style={{ ...cell, color: d.source === "return" ? "#7ab97a" : "#9a9a9a" }} className="text-xs">{d.source || "—"}</td>
            </tr>
          );
        })}
      />
    </>
  );
}
